import { ProtectedRoute } from "./components/ProtectedRoute";
import { LoginPage } from "./pages/LoginPage";
import { DashboardPage } from "./pages/DashboardPage";
import { PersonalData } from "./pages/PersonalData";
import { CompanyData } from "./pages/CompanyData";
import { CompanyUserOverview } from "./pages/CompanyUserOverview";
import { CompanyUserEdit } from "./pages/CompanyUserEdit";
import { PartnerOverview } from "./pages/PartnerOverview";
import {PartnerDisplay, PartnerDisplayBreadcrumb} from "./pages/PartnerDisplay";
import { ClientDisplay, ClientDisplayBreadcrumb } from "./pages/ClientDisplay";
import { ClientOverview } from "./pages/ClientOverview";
import { ClientPartnerDisplay, ClientPartnerDisplayBreadcrumb } from "./pages/ClientPartnerDisplay";
import { NetworkParticipantsPage } from "./pages/NetworkParticipantsPage";
import { FileExchange } from "./pages/FileExchange";
import { NotFoundPage } from "./pages/NotFoundPage";
import {ClientPartnerTestBreadcrumb, TestPage} from "./pages/TestPage";
import { Navigate, Outlet, Params } from "react-router";
import React from "react";
import { AppContextType } from "./context/AppContext";

/**
 * route item type
 */
export interface RouteItem {
  id?: string;
  nav?: any;
  index?: any;
  handle?: any;
  loader?: any;
  icon?: string;
  children?: any;
  title?: string;
  path: string;
  element: any;
  params?: any;
}

/**
 * route items type
 */
export interface RouteItems {
  [key: string]: RouteItem
}

/**
 * route item internal type
 */
export interface RouteItemInternal {
  path: string
  key: string
  element: any
  children: any | undefined;
  loader: any;
  handle: {
    title: string | undefined
    icon: string | undefined
  };
}

/**
 * ROUTES
 * @param I18N
 * @param I18NisReady
 * @constructor
 */
export default function ROUTES(I18N ,I18NisReady: boolean): RouteItems {
  /**
   * route list
   */
  return I18NisReady ? {
    ROOT: {
      path: "/",
      element: <Navigate replace to="/dashboard" />
    },
    LOGIN: {
      title: I18N.t("Login"),
      path: "/login",
      element: <LoginPage />
    },
    LOGIN_SSO: {
      title: I18N.t("Login SSO"),
      path: "/login/sso",
      element: <LoginPage />
    },
    PASSWORD_RESET: {
      title: I18N.t("Passwort zurücksetzen"),
      path: "/reset-password",
      element: <LoginPage />
    },
    NETWORK_PARTICIPANTS: {
      title: I18N.t("Netzwerkteilnehmer"),
      path: "/network-participants",
      element: <NetworkParticipantsPage />
    },
    DASHBOARD: {
      title: I18N.t("Übersicht"),
      path: "/dashboard",
      element: <ProtectedRoute><DashboardPage /></ProtectedRoute>,
      icon: "dashboard",
      handle: {
        breadcrumb: (route: RouteItem) => route.title
      },
      nav: true
    },
    PARTNER_OVERVIEW: {
      title: I18N.t("Partner"),
      path: "/partner",
      element: <ProtectedRoute><PartnerOverview /></ProtectedRoute>,
      icon: "handshake",
      handle: {
        breadcrumb: (route: RouteItem) => route.title
      },
      children: [
        'PARTNER_VIEW_EDIT'
      ],
      nav: true
    },
    PARTNER_VIEW_EDIT: {
      title: I18N.t("Partner bearbeiten"),
      path: "/partner/edit/:id/:type",
      element: <ProtectedRoute><PartnerDisplay /></ProtectedRoute>,
      icon: "link-horizontal",
      handle: {
        breadcrumb: (
          route: RouteItem,
          context: AppContextType,
          params: Params<string>
        ) => PartnerDisplayBreadcrumb(route, context, params)
      }
    },
    FILE_EXCHANGE: {
      title: I18N.t("Dateiaustausch"),
      path: "/file-exchange",
      element: <ProtectedRoute><FileExchange /></ProtectedRoute>,
      icon: "folder-open",
      handle: {
        breadcrumb: (route: RouteItem) => route.title
      },
      nav: true
    },
    CLIENT_OVERVIEW: {
      title: I18N.t("Clients"),
      path: "/client",
      element: <ProtectedRoute><ClientOverview /></ProtectedRoute>,
      icon: "computer",
      handle: {
        breadcrumb: (route: RouteItem) => route.title
      },
      children: [
        'CLIENT_VIEW_EDIT',
        'CLIENT_TEST'
      ],
      nav: true
    },
    CLIENT_VIEW_EDIT: {
      title: I18N.t("Client bearbeiten"),
      path: "/client/edit/:id",
      element: <ProtectedRoute><ClientDisplay /></ProtectedRoute>,
      icon: "pen-to-square",
      handle: {
        breadcrumb: (
          route: RouteItem,
          context: AppContextType,
          params: Params<string>
        ) => ClientDisplayBreadcrumb(route, context, params)
      },
      children: [
        'CLIENT_PARTNER_VIEW_EDIT',
        'CLIENT_PARTNER_TEST',
        'CLIENT_TEST'
      ]
    },
    CLIENT_TEST: {
      title: I18N.t("API Test"),
      path: "/client/edit/:id/test",
      element: <ProtectedRoute><TestPage /></ProtectedRoute>,
      icon: "laptop-code",
      handle: {
        breadcrumb: (route: RouteItem) => route.title
      }
    },
    CLIENT_PARTNER_VIEW_EDIT: {
      title: I18N.t("Client Partner bearbeiten"),
      path: "/client/edit/:id/clientpartner/:partnerId/:type",
      element: <ProtectedRoute><ClientPartnerDisplay /></ProtectedRoute>,
      icon: "pen-to-square",
      handle: {
        breadcrumb: (
          route: RouteItem,
          context: AppContextType,
          params: Params<string>
        ) => ClientPartnerDisplayBreadcrumb(route, context, params)
      }
    },
    CLIENT_PARTNER_TEST: {
      title: I18N.t("API Test"),
      path: "/client/edit/:id/clientpartner/test/:partnerHash/:partnerId/:type",
      element: <ProtectedRoute><TestPage /></ProtectedRoute>,
      icon: "laptop-code",
      handle: {
        breadcrumb: (
          route: RouteItem,
          context: AppContextType,
          params: Params<string>
        ) => ClientPartnerTestBreadcrumb(route, context, params)
      }
    },
    COMPANY_USER_OVERVIEW: {
      title: I18N.t("Benutzer"),
      path: "/company-user",
      element: <ProtectedRoute><CompanyUserOverview /></ProtectedRoute>,
      icon: "users",
      handle: {
        breadcrumb: (route: RouteItem) => route.title
      },
      children: [
        'COMPANY_USER_VIEW_EDIT'
      ],
      nav: true
    },
    COMPANY_USER_VIEW_EDIT: {
      title: I18N.t("Benutzer bearbeiten"),
      path: "/company-user/edit/:id",
      element: <ProtectedRoute><CompanyUserEdit /></ProtectedRoute>,
      icon: "pen-to-square",
      handle: {
        breadcrumb: (route: RouteItem) => route.title
      }
    },
    COMPANY_DATA: {
      title: I18N.t("Firma"),
      path: "/company-data",
      element: <ProtectedRoute><CompanyData /></ProtectedRoute>,
      icon: "building-user",
      handle: {
        breadcrumb: (route: RouteItem) => route.title
      },
      nav: true
    },
    PERSONAL_DATA: {
      title: I18N.t("Meine Daten"),
      path: "/personal-data",
      element: <ProtectedRoute><PersonalData /></ProtectedRoute>,
      icon: "user",
      handle: {
        breadcrumb: (route: RouteItem) => route.title
      },
      nav: true
    },
    NOT_FOUND: {
      title: I18N.t("Seite nicht gefunden!"),
      path: "*",
      element: <NotFoundPage />,
      handle: {
        breadcrumb: (route: RouteItem) => route.title
      }
    }
  } as RouteItems: {};
}

/**
 * ROUTE_CHILDREN
 */
export function ROUTE_CHILDREN(I18N ,I18NisReady: boolean): RouteItemInternal[] {
  /**
   * we create a list of all children defined in routes.tsx
   * with all possible values defined for it otherwise it will be undefined
   * this format is used in react router
   * handle data is available on any stage in application by useMatches
   */
  let routes: RouteItems = ROUTES(I18N ,I18NisReady),
    routeItems: RouteItem[] = [];

  Object.keys(routes).forEach((name: string) => routes[name].id = name);
  routeItems = Object.values(routes);

  /**
   * processChildren
   * @param route
   */
  let processChildren = (route) => {
    route.children = route.children?.map((routeName) => processRouteDefinition(routeName));

    if (route.children) {
      /**
       * create layout route with outlet
       */
      let element: JSX.Element = route.element;
      if (element) {
        route.element = <Outlet/>;
      }

      /**
       * index to children with previous element of layout route
       */
      route.children = [
        {
          index: true,
          element: element
        },
        ...route.children
      ];
    }
  };

  /**
   * processRouteDefinition
   * @param routeName
   */
  let processRouteDefinition = (routeName: string): RouteItemInternal => {
    if (routes[routeName] === undefined) {
      console.error("route with name '" + routeName + "' is not defined!");
    }

    let _routeByName: RouteItem = routes[routeName],
      _routeItemSourceIndex: number | undefined = _routeByName ? routeItems.findIndex(
        (_routeItem: RouteItem): boolean => _routeItem && _routeByName && _routeItem.path === _routeByName.path
      ) : undefined;

    if (_routeByName.children !== undefined) {
      processChildren(_routeByName);
    }

    if (_routeItemSourceIndex) {
      delete routeItems[_routeItemSourceIndex];
    }

    return ROUTE_ITEM_INTERNAL(_routeByName);
  };

  routeItems.forEach((route: RouteItem): void => {
    processChildren(route);
  });

  return routeItems.map((route: RouteItem): RouteItemInternal => ROUTE_ITEM_INTERNAL(route));
}

/**
 * ROUTE_ITEM_INTERNAL
 * @param route
 * @constructor
 */
function ROUTE_ITEM_INTERNAL(route: RouteItem): RouteItemInternal {
  return {
    index: route.index,
    path: route.path,
    key: route.title,
    element: route.element,
    children: route.children,
    loader: route.loader,
    handle: {
      ...route.handle,
      route: {
        title: route.title,
        icon: route.icon,
        id: route.id
      }
    }
  } as RouteItemInternal
}
